import { NEW_LOOK_RADIUS } from "common/NewLook/tokens.ts";
import Title from "common/Typography/Title";
import Card from "common/Boxes/Card";
import styled, { useTheme } from "styled-components";
import {
  PhoneCard,
  PhoneSectionHead,
} from "features/dashboard/PhoneDashboard/PhoneParts";
import {
  LARGE_MOBILE,
  MOBILE_MENU,
  SMALL_MOBILE,
  SMALL_SCREEN,
  TIME_RANGE_1_DAY,
  WATCHLIST_PATH,
} from "common/constants";
import { Link } from "react-router-dom";
import React, { useContext, useEffect, useState } from "react";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { StyledOpenInNewIcon } from "features/dashboard/common/styles";
import { RawLineChartDataPoint } from "types";
import TopMarket from "features/dashboard/Market/TopMarket";
import NewLookTopMarkets from "features/dashboard/Market/NewLookTopMarkets.tsx";
import Panel from "common/NewLook/Panel.tsx";
import SegmentTabs from "common/NewLook/SegmentTabs.tsx";
import MarketsList from "features/dashboard/instrument/MarketsList.tsx";
import { useBottomNavigator } from "utils/hooks/misc";
import Col from "antd/es/col";
import AntRow from "antd/es/row";
import useMedia from "utils/hooks/useMedia";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { cloneDeep } from "lodash";
import { AnalyticsContext } from "analytics/AnalyticsProvider";
import { MarketsTableView } from "analytics/events";
import { hexToRGB } from "@shared/utils/misc";
import {
  CoinName,
  ExchangeName,
  LanguageNameSpaces,
  TopMarketsTableData,
  TopMarketsTableDataBySymbol,
  TopMarketsTabs,
} from "@shared/types";
import { getCoinExtraDetailsRoute } from "utils/routes";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery";
import { sharedLib } from "@shared/services/shared.service";

const MarketTable = () => {
  const { isNewLook } = useTheme();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const { analytics } = useContext(AnalyticsContext);
  const isMobile = useMedia([`(max-width: ${LARGE_MOBILE})`], [true], false);
  const isTablet = useMedia([`(max-width: ${MOBILE_MENU})`], [true], false);
  const isSmallScreen = useMedia(
    [`(max-width: ${SMALL_SCREEN})`],
    [true],
    false
  );
  const [initialRender, setInitialRender] = useState(true);

  const { t } = useTypedTranslation(LanguageNameSpaces.DASHBOARD);
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);

  const { data, isLoading: getTopMarketsLoading } =
    sharedLib.sharedApi.dashboard.useGetTopMarketsQuery();

  const [selectedTab, setSelectedTab] = useState<TopMarketsTabs>(
    TopMarketsTabs.TOP_GAINERS
  );

  const { historyPush } = useHistoryWithQuery();

  const openCoinDrawer = (symbol: CoinName) => {
    return historyPush(getCoinExtraDetailsRoute(location.pathname, symbol));
  };

  useEffect(() => {
    const analyticsMapping = {
      [TopMarketsTabs.TOP_LOSERS]: MarketsTableView.TOP_LOSERS,
      [TopMarketsTabs.TOP_GAINERS]: MarketsTableView.TOP_GAINERS,
      [TopMarketsTabs.VOLUME]: MarketsTableView.VOLUME,
    };
    if (!initialRender) {
      const marketsTableView = analyticsMapping[selectedTab];
      analytics?.dashboardMarketsTableViewChanged({
        marketsTableView,
      });
    }
    setInitialRender(false);
  }, [selectedTab]);

  const getSum = (initialValue: number, newValue: number) => {
    let sum = initialValue;
    if (newValue !== null) {
      if (sum === null) {
        sum = newValue;
      } else sum += newValue;
    }
    return sum;
  };

  const getSumOfArray = (
    initialArray: [number, number],
    newArray: [number, number]
  ) => {
    for (let index in initialArray) {
      initialArray[index] = getSum(initialArray[index], newArray[index]);
    }
    return initialArray;
  };

  const getGraphDataSum = (
    graphData: RawLineChartDataPoint[],
    newGraphData: RawLineChartDataPoint[]
  ) => {
    for (let point in graphData) {
      if (graphData[point] && newGraphData[point]) {
        graphData[point][1] = getSum(
          graphData[point][1],
          newGraphData[point][1]
        );
      }
    }
    return graphData;
  };

  const addDataToExistSymbol = (
    symbol: TopMarketsTableDataBySymbol,
    symbolNewData: TopMarketsTableData
  ) => {
    symbol.exchanges.push(symbolNewData.exchange);
    symbol.change = getSum(symbol.change, symbolNewData.graph_data.change);
    symbol.high_price = getSumOfArray(
      symbol.high_price,
      symbolNewData.high_price
    );
    symbol.low_price = getSumOfArray(symbol.low_price, symbolNewData.low_price);
    symbol.price = getSum(symbol.price, symbolNewData.price);
    symbol.volume = getSumOfArray(symbol.volume, symbolNewData.volume);
    symbol.graph_data = getGraphDataSum(
      symbol.graph_data,
      symbolNewData.graph_data[TIME_RANGE_1_DAY]
    );
    return symbol;
  };

  const getAverage = (sum: number, numOfValues: number) => {
    if (sum !== null) return sum / numOfValues;
    return sum;
  };

  const getValuesAverage = (symbols: TopMarketsBySymbol) => {
    for (let symbol in symbols) {
      const numOfExchanges = symbols[symbol].exchanges.length;
      symbols[symbol].change = getAverage(
        symbols[symbol].change,
        numOfExchanges
      );
      symbols[symbol].price = getAverage(symbols[symbol].price, numOfExchanges);
      symbols[symbol].high_price = [
        getAverage(symbols[symbol].high_price[0], numOfExchanges),
        getAverage(symbols[symbol].high_price[1], numOfExchanges),
      ];
      symbols[symbol].low_price = [
        getAverage(symbols[symbol].low_price[0], numOfExchanges),
        getAverage(symbols[symbol].low_price[1], numOfExchanges),
      ];
      for (let point in symbols[symbol].graph_data) {
        symbols[symbol].graph_data[point][1] = getAverage(
          symbols[symbol].graph_data[point][1],
          numOfExchanges
        );
      }
    }
    return symbols;
  };

  const getMarketsArrayByTab = (exchangeName: ExchangeName) => {
    if (selectedTab === TopMarketsTabs.TOP_GAINERS)
      return data?.[exchangeName].top_gainers;
    if (selectedTab === TopMarketsTabs.TOP_LOSERS)
      return data?.[exchangeName].top_losers;
    if (selectedTab === TopMarketsTabs.VOLUME)
      return data?.[exchangeName].top_volume;
  };

  const getTopMarketsBySymbols = () => {
    let symbols: TopMarketsBySymbol = {};
    if (data) {
      for (let exchangeName in data) {
        const array = getMarketsArrayByTab(exchangeName);
        if (array) {
          for (let market of array) {
            const symbol = market.symbol;
            if (!Object.keys(symbols).includes(symbol)) {
              let exchanges: ExchangeName[] = [];
              exchanges.push(market.exchange);
              symbols[symbol] = {
                exchanges: exchanges,
                graph_data: cloneDeep(market.graph_data[TIME_RANGE_1_DAY]),
                change: market.graph_data.change,
                high_price: [market.high_price[0], market.high_price[1]],
                low_price: [market.low_price[0], market.low_price[1]],
                price: market.price,
                volume: [market.volume[0], market.volume[1]],
                symbol: symbol,
              };
            } else {
              symbols[symbol] = addDataToExistSymbol(symbols[symbol], market);
            }
          }
        }
      }
    }
    return getValuesAverage(symbols);
  };

  const getArrayOfSymbols = () => {
    const symbols = getTopMarketsBySymbols();
    let symbolsArray: TopMarketsTableDataBySymbol[] = [];
    for (let symbol in symbols) {
      symbolsArray.push(symbols[symbol]);
    }
    return symbolsArray;
  };

  const getSortedMarketsByVolume = () => {
    return getArrayOfSymbols()
      .slice()
      .sort((a, b) => (b.volume[0] || 0) - (a.volume[0] || 0));
  };

  const getSortedMarketsByChange = (tab: TopMarketsTabs) => {
    return getArrayOfSymbols()
      .slice()
      .sort((a, b) => {
        if (tab === TopMarketsTabs.TOP_GAINERS)
          return (b.change || 0) - (a.change || 0);
        return (a.change || 0) - (b.change || 0);
      });
  };

  const getTableData = () => {
    let sortedArray: TopMarketsTableDataBySymbol[] = [];
    if (selectedTab === TopMarketsTabs.TOP_GAINERS)
      sortedArray = getSortedMarketsByChange(TopMarketsTabs.TOP_GAINERS);
    if (selectedTab === TopMarketsTabs.TOP_LOSERS)
      sortedArray = getSortedMarketsByChange(TopMarketsTabs.TOP_LOSERS);
    if (selectedTab === TopMarketsTabs.VOLUME)
      sortedArray = getSortedMarketsByVolume();
    return sortedArray.slice(0, 5);
  };

  const getMarketTableTabs = () => {
    return (
      <TabsWrapper>
        {Object.values(TopMarketsTabs).map((tab, i) => (
          <TabTitle
            level={isMobile ? 4 : 3}
            key={i}
            onClick={() => setSelectedTab(tab)}
            $selected={selectedTab === tab}
            $isMobile={isMobile}
          >
            {t(tab)}
          </TabTitle>
        ))}
      </TabsWrapper>
    );
  };

  const marketTableHeaders = (
    <Row>
      <Col span={isMobile ? 9 : isSmallScreen ? 4 : 5}>
        <HeaderTitle level={isSmallScreen ? 5 : 4}>
          {t("market.header.pair")}
        </HeaderTitle>
      </Col>
      <Col span={isMobile ? 9 : isSmallScreen ? 4 : 5}>
        <HeaderTitle level={isSmallScreen ? 5 : 4}>
          {t("market.header.last_price")}
        </HeaderTitle>
      </Col>
      <Col
        span={isMobile ? 6 : isSmallScreen ? 4 : 5}
        style={{ textAlign: isSmallScreen ? "center" : "start" }}
      >
        <HeaderTitle level={isSmallScreen ? 5 : 4}>
          {t("market.header.24h_change")}
        </HeaderTitle>
      </Col>
      {!isMobile && (
        <>
          <Col
            span={isSmallScreen ? 7 : 6}
            style={{ textAlign: isSmallScreen ? "center" : "start" }}
          >
            <HeaderTitle level={isSmallScreen ? 5 : 4}>
              {t("market.header.24h_low_high")}
            </HeaderTitle>
          </Col>
          <Col
            span={isSmallScreen ? 5 : 3}
            style={{ textAlign: isSmallScreen ? "end" : "start" }}
          >
            <HeaderTitle level={isSmallScreen ? 5 : 4}>
              {t("market.header.24h_volume")}
            </HeaderTitle>
          </Col>
        </>
      )}
    </Row>
  );

  const tableRows = (
    <LoadingSpinnerOverlay
      isLoading={getTopMarketsLoading}
      backgroundColor="inherit"
      spinnerMarginTop={75}
    >
      {getTableData()?.map((symbol, index) => (
        <TopMarketWrapper
          key={index}
          onClick={() => openCoinDrawer(symbol.symbol)}
        >
          <TopMarket symbol={symbol} />
        </TopMarketWrapper>
      ))}
    </LoadingSpinnerOverlay>
  );

  if (isNewLook && !isBottomNavigator) {
    return (
      <Panel
        title={t("market.section_title")}
        actions={
          <SegmentTabs
            variant="underline"
            options={Object.values(TopMarketsTabs)}
            value={selectedTab}
            onChange={setSelectedTab}
            getLabel={(tab) => t(tab)}
            ariaLabel={t("market.section_title")}
          />
        }
        seeAllTo={WATCHLIST_PATH}
        seeAllLabel={tCommon("labels.see_all")}
        flush={true}
        data-testid="dashboard-markets"
      >
        <MarketsList
          rows={getTableData()}
          loading={getTopMarketsLoading}
          onOpen={openCoinDrawer}
        />
      </Panel>
    );
  }

  if (isNewLook) {
    return (
      <section>
        <PhoneSectionHead
          title={t("market.section_title")}
          seeAllTo={WATCHLIST_PATH}
          seeAllLabel={tCommon("labels.see_all")}
        />
        <PhoneTabs>
          <NewLookTabs>
            {Object.values(TopMarketsTabs).map((tab) => (
              <NewLookTab
                key={tab}
                type="button"
                onClick={() => setSelectedTab(tab)}
                $selected={selectedTab === tab}
              >
                {t(tab)}
              </NewLookTab>
            ))}
          </NewLookTabs>
        </PhoneTabs>
        <PhoneCard style={{ padding: 0 }}>
          <NewLookTopMarkets
            rows={getTableData().slice(0, isMobile ? 3 : 5)}
            loading={getTopMarketsLoading}
            onOpen={openCoinDrawer}
          />
        </PhoneCard>
      </section>
    );
  }

  return (
    <Wrapper $isTablet={isTablet}>
      <TitleWrapper>
        <SectionTitle level={2} type="secondary">
          {t("market.section_title")}
        </SectionTitle>
        <Link to={WATCHLIST_PATH}>
          <StyledOpenInNewIcon />
        </Link>
      </TitleWrapper>
      {getMarketTableTabs()}
      <div style={{ overflow: "auto" }}>
        <TableCard $isMobile={isMobile}>
          {marketTableHeaders}
          {tableRows}
        </TableCard>
      </div>
    </Wrapper>
  );
};

export default MarketTable;

type TopMarketsBySymbol = {
  [key in CoinName]: TopMarketsTableDataBySymbol;
};

const Wrapper = styled.div<{ $isTablet: boolean }>`
  border: ${(p) =>
    p.$isTablet ? "unset" : `3px solid ${p.theme.colors.lightestBorderGrey}`};
  border-radius: 8px;
  margin: ${(p) => (p.$isTablet ? 0 : 32)}px;
  padding: ${(p) => (p.$isTablet ? "0 24px 16px" : "16px")};
  @media (max-width: ${SMALL_MOBILE}) {
    padding: 0 12px 16px;
  }
`;
const TableCard = styled(Card)<{ $isMobile: boolean }>`
  background: ${(p) => p.theme.colors.cardBgColor};
  border: ${(p) => p.theme.borders.lightest};
  box-shadow: none;
  border-radius: 8px;
  height: auto;
  position: relative;
  min-height: 125px;
  margin-bottom: 12px;
  width: auto;
  padding: ${(p) => (p.$isMobile ? "16px 16px 12px" : "16px 24px")};
  @media (max-width: ${SMALL_MOBILE}) {
    padding: 16px 12px 12px;
  }
`;
const TitleWrapper = styled.div`
  display: flex;
  align-items: baseline;
  margin-bottom: 20px;
`;
const SectionTitle = styled(Title)`
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
  margin: 0 8px;
`;
const TabsWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
`;
const TabTitle = styled(Title)<{
  $selected: boolean;
  $isMobile: boolean;
}>`
  cursor: pointer;
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
  color: ${(p) =>
    p.$selected ? p.theme.colors.primaryBlue : p.theme.colors.secondaryGrey};
  background: ${(p) =>
    p.$selected ? hexToRGB(p.theme.colors.primaryBlue, 0.1) : ""};
  padding: ${(p) => (p.$isMobile ? "4px 16px" : "4px 28px")};
  border-radius: 8px;
  &:not(:last-child) {
    margin-right: ${(p) => (p.$isMobile ? 0 : 20)}px;
  }
`;
const Row = styled(AntRow)`
  width: 100%;
  display: flex;
  align-items: center;
`;
const TopMarketWrapper = styled.div`
  padding-top: 12px;
  cursor: pointer;
  &:not(:last-child) {
    padding-bottom: 12px;
    border-bottom: ${(p) => p.theme.borders.lightest};
  }
`;
const HeaderTitle = styled(Title).attrs({
  type: "secondary",
})``;
const NewLookTabs = styled.div`
  display: inline-flex;
  padding: 3px;
  border-radius: ${(p) => p.theme.radii.control}px;
  background: ${(p) => p.theme.colors.cardLightBgColor};
  border: ${(p) => p.theme.borders.lightest};
`;
const NewLookTab = styled.button<{ $selected: boolean }>`
  border: 0;
  padding: 4px 12px;
  border-radius: ${NEW_LOOK_RADIUS.chip}px;
  font: inherit;
  font-size: 12.5px;
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  cursor: pointer;
  color: ${(p) =>
    p.$selected ? p.theme.colors.primaryText : p.theme.colors.secondaryText};
  background: ${(p) =>
    p.$selected ? p.theme.colors.cardBgColor : "transparent"};
  box-shadow: ${(p) => (p.$selected ? p.theme.shadows.dashboardCard : "none")};
`;
const PhoneTabs = styled.div`
  display: flex;
  padding: 0 16px 10px;
`;
